import React, {useEffect, useState, useContext} from 'react';
import {API_URL} from '../../../constants';
import {handleFiles} from '../../../util/FileUtils';
import {securedFetchRequest} from '@bidmii/common/lib/util/FetchRequest';
import {Box, Grid, Slide, Typography} from '@mui/material';
import HorizontalThumbnailImageList from '../../../common/HorizontalThumbnailImageList';
import {fireScreenView, SCREEN_NAMES} from '../../../util/Analytics';
import AlertDialog from '../../../common/dialogs/alertDialog';
import {theme} from '../../../Theme';
import TertiaryButton from '../../../common/StyledComponents/TertiaryButton';
import DocumentsList from '../../../common/DocumentsList';
import {AttachFile} from '@mui/icons-material';
import {NewProjectContext} from '../utils/NewProjectContext';
import ActionButtons from '../components/ActionButtons';

interface IProps {
  projectSubmitCallback: any;
  changeStepHandler: (number: number) => void;
  forward: boolean;
}

const ImageUploadStep = ({
  projectSubmitCallback,
  changeStepHandler,
  forward,
}: IProps) => {
  useEffect(() => {
    fireScreenView(SCREEN_NAMES.NEW_PROJECT_WIZARD_STEP_TWO);
  }, []);

  const {project, setProject, homeDepot} = useContext(NewProjectContext);

  const imageGalleryRef = React.useRef();
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const skipImage = () => {
    if (project.templateId?.includes('designmii') || homeDepot) {
      projectSubmitCallback();
    } else {
      changeStepHandler(1);
    }
  };

  const onImageCapture = (
    event: $TSFixMe,
    key: string,
    description: string,
  ) => {
    setLoading(true);
    setDisabled(true);
    const attachmentInfo = {
      key,
      description,
      resModel: 'RequestForProposal',
      resId: `${project.id}`,
    };
    handleFiles(event, updateRfp, attachmentInfo);
  };

  const updateRfp = () => {
    getData(project.uid);
  };

  const getData = (rfp_id: $TSFixMe) => {
    setLoading(true);
    const url = `${API_URL}/my/rfps/${rfp_id}`;
    securedFetchRequest(url, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((res) => {
        localStorage.setItem('rfp', JSON.stringify(res));
        setProject(res);
        setLoading(false);
        setDisabled(false);
      });
  };

  const handleClickBack = () => {
    changeStepHandler(-1);
  };

  const handleClickNext = () => {
    if (
      project.attachmentReferences?.filter(
        (ref: any) => ref.key === 'before_image',
      ).length === 0
    ) {
      setConfirmOpen(true);
    } else {
      skipImage();
    }
  };

  return (
    <>
      <Slide in={true} direction={forward ? 'left' : 'right'} timeout={400}>
        <div style={{width: '100%', maxWidth: 450}}>
          <Typography variant="h6" fontSize={22} fontWeight="bold">
            Upload images
          </Typography>
          <Box mt={2} mb={3}>
            <Typography
              variant="subtitle1"
              fontSize={15}
              lineHeight={1.4}
              color={theme.palette.primary.light}>
              Upload a few photos of the existing conditions. Show the whole
              space. You can also add documents such as floor plans, if you have
              them.
            </Typography>
          </Box>
          {/* <form noValidate autoComplete="off">
          <input
            accept="image/*, .mp4, .avi, .webm"
            id="myproject-image-file"
            type="file"
            multiple
            onChange={onImageCapture}
            style={{display: 'none'}}
          />
          <label htmlFor="myproject-image-file">
            <SecondaryButton
              size="small"
              aria-label="Upload images"
              component="span">
              <ImageIcon style={{marginRight: 6}} />
              UPLOAD IMAGES
            </SecondaryButton>
          </label>
        </form> */}

          <Box
            mb={4}
            style={{maxHeight: '40vh', minHeight: 242, maxWidth: '100%'}}>
            <HorizontalThumbnailImageList
              imageGalleryRef={imageGalleryRef}
              data={project.attachmentReferences?.filter(
                (ref: any) => ref.key === 'before_image',
              )}
              onImageCapture={(e: $TSFixMe) =>
                onImageCapture(e, 'before_image', 'Before image')
              }
              updateRec={updateRfp}
              resModel={'RequestForProposal'}
              resId={project.id}
              setLoading={setLoading}
              inputId="upload-image-file"
            />
          </Box>

          {project.templateId?.includes('designmii') && (
            <>
              <Typography
                variant="subtitle1"
                fontSize={15}
                lineHeight={1.4}
                color={theme.palette.primary.light}>
                Upload 4-6 inspirational images of rooms and designs you love.
              </Typography>
              <Box
                mt={1}
                mb={4}
                style={{maxHeight: '40vh', minHeight: 242, maxWidth: '100%'}}>
                <HorizontalThumbnailImageList
                  imageGalleryRef={imageGalleryRef}
                  data={project.attachmentReferences?.filter(
                    (ref: any) => ref.key === 'inspiration',
                  )}
                  onImageCapture={(e: $TSFixMe) =>
                    onImageCapture(e, 'inspiration', 'Inspiration image')
                  }
                  updateRec={updateRfp}
                  resModel={'RequestForProposal'}
                  resId={project.id}
                  setLoading={setLoading}
                  inputId="upload-image-file_inspiration"
                />
              </Box>
            </>
          )}

          <Box mb={2}>
            <input
              style={{display: 'none'}}
              id="project-attachment"
              type="file"
              onChange={(e) =>
                onImageCapture(e, 'project_document', 'Project document')
              }
            />
            <label htmlFor="project-attachment">
              <Grid container alignItems="center">
                <AttachFile fontSize="small" />
                <TertiaryButton
                  bold
                  aria-label="Upload documents"
                  component="span">
                  Upload Document (optional)
                </TertiaryButton>
              </Grid>
            </label>
            <Box mt={1}>
              <DocumentsList
                attachmentReferences={project.attachmentReferences?.filter(
                  (ref: any) => ref.key === 'project_document',
                )}
                editable
              />
            </Box>
          </Box>

          <ActionButtons
            secondaryAction={handleClickBack}
            primaryAction={handleClickNext}
            primaryLabel={homeDepot ? 'Submit' : 'NEXT'}
            disabled={disabled}
            loading={loading}
          />
        </div>
      </Slide>

      <AlertDialog
        open={confirmOpen}
        title="Are you sure you want to skip this step?"
        body="Images are required before contractors can bid on your project."
        confirm="ADD LATER"
        close="CANCEL"
        onConfirm={skipImage}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default ImageUploadStep;
